import { MapPin, Phone } from "lucide-react";
import React from "react";
import GoogleMapViewOnly from "../../components/GoogleMapViewOnly";
import { Config } from "../../utils/config";
import { useTranslation } from "react-i18next";
import { convertHoursToObject } from "../../utils/convert-hours-to-object";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";

export default function About({ authToken, restaurantDetail, id }) {
  const { t } = useTranslation();
  const deliveryPostalCodesRaw = restaurantDetail?.deliveryPostalCodes;
  const currency = restaurantDetail?.currencyDetails?.symbol;
  const deliveryPostalCodes = deliveryPostalCodesRaw?.delivery_postal_codes.map(
    (item) => {
      return {
        postalCode: item?.locality_name
          ? item?.locality_name
          : item.postal_code,
        minOrder: `${t("about.min")} ${currency} ${Number(
          item.minimum_order_amount
        ).toFixed(2)}`,
        deliveryFee: `${t("about.fee")} ${currency} ${Number(
          item.delivery_fee
        ).toFixed(2)}`,
      };
    }
  );

  const openingHoursRaw = restaurantDetail?.openingHours?.opening_hours;
  const deliveryHoursRaw = restaurantDetail?.openingHours?.delivery_hours;
  const pickupHoursRaw = restaurantDetail?.openingHours?.pickup_hours;
  const tableReservationHoursRaw =
    restaurantDetail?.openingHours?.table_reservation_hours;
  const orderAheadHoursRaw = restaurantDetail?.services?.scheduled_orders;

  const openingHours = convertHoursToObject({ hours: openingHoursRaw });
  const deliveryHours = convertHoursToObject({ hours: deliveryHoursRaw });
  const pickupHours = convertHoursToObject({ hours: pickupHoursRaw });
  const tableReservationHours = convertHoursToObject({
    hours: tableReservationHoursRaw,
  });

  const address = restaurantDetail?.address;

  const phoneNumber = `${restaurantDetail?.phoneCountryCode} ${restaurantDetail?.phone}`;

  const checkDeliveryHoursRemainSameAsOpeningHours =
    JSON.stringify(openingHours) === JSON.stringify(deliveryHours);

  const checkPickupHoursRemainSameAsOpeningHours =
    JSON.stringify(openingHours) === JSON.stringify(pickupHours);

  const checkTableReservationHoursRemainSameAsOpeningHours =
    JSON.stringify(openingHours) === JSON.stringify(tableReservationHours);

  const paymentMethods = restaurantDetail?.paymentMethods;

  const paymentMethodsArray = Config.env.app.payment_method
    .map((item) => {
      if (
        item.value === "pay_online" &&
        !restaurantDetail?.services?.online_payment
      ) {
        // Skip the item if the condition is not matched
        return null;
      }
      item.value =
        item.value === "call_me_back"
          ? "call_me"
          : item.value === "qr_code_scan"
          ? "qr_code"
          : item.value;

      if (paymentMethods && paymentMethods[item.value]) {
        const paymentMethodDetails = paymentMethods[item.value];
        const includedOrderingMethod = Object.keys(paymentMethodDetails).filter(
          (key) => paymentMethodDetails[key] === true
        );

        const updatedIncludedOrderingMethod =
          Config.env.app.about_ordering_method
            .map((method) => {
              if (includedOrderingMethod.includes(method.value)) {
                return t(method.title);
              }
              return null;
            })
            .filter((item) => item !== null);

        if (includedOrderingMethod.length > 0) {
          return {
            label: `${t(item.label)} ${
              updatedIncludedOrderingMethod.length > 0
                ? `(${updatedIncludedOrderingMethod.join(", ")})`
                : ""
            }`,
            value: item.value,
            icon: item.icon,
          };
        }
      }
      return {
        label: t(item.label),
        value: item.value,
        icon: item.icon,
      };
    })
    .filter((item) => item !== null); // Filter out null values

  return (
    <div className="container">
      <div className=" space-y-4">
        <div className="aspect-[3/1]">
          <GoogleMapViewOnly
            lat={Number(restaurantDetail?.lat)}
            lng={Number(restaurantDetail?.lng)}
          />
        </div>
        {/* accordion grid */}
        <div className="grid lg:grid-cols-2 gap-5">
          {/* delivery fees card */}
          <Accordion type="single" collapsible>
            <AccordionItem
              value={t("about.delivery_fees")}
              className="border-b-0"
            >
              <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
                <h3 className="text-red font-semibold text-lg sm:text-xl">
                  {t("about.delivery_fees")}
                </h3>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="list-none space-y-1">
                  {deliveryPostalCodes?.map((item, index) => (
                    <li key={index} className="leading-relaxed">
                      <span className="font-semibold">{item.postalCode}</span>,{" "}
                      {item.minOrder}, {item.deliveryFee}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          {/* opening hours card */}
          <Accordion type="single" collapsible>
            <AccordionItem
              value={t("about.opening_hours")}
              className="border-b-0"
            >
              <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
                <h3 className="text-red font-semibold text-lg sm:text-xl">
                  {t("about.opening_hours")}
                </h3>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {openingHours.map((day) => (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">{day.day}</span>
                      <div className="text-right">
                        {day.hours.map((hour) => (
                          <div className="">{hour}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          {/* delivery card */}
          <Accordion type="single" collapsible>
            <AccordionItem value={t("about.delivery")} className="border-b-0">
              <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
                <h3 className="text-red font-semibold text-lg sm:text-xl">
                  {t("about.delivery")}
                </h3>
              </AccordionTrigger>
              <AccordionContent>
                {checkDeliveryHoursRemainSameAsOpeningHours === true ? (
                  <p>{t("about.same_as_opening_hours")}</p>
                ) : (
                  <div className="space-y-2">
                    {deliveryHours.map((day) => (
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">{day.day}</span>
                        <div className="text-right">
                          {day.hours.map((hour) => (
                            <div className="">{hour}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* pickup card */}
          <Accordion type="single" collapsible>
            <AccordionItem value={t("about.pickup")} className="border-b-0">
              <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
                <h3 className="text-red font-semibold text-lg sm:text-xl">
                  {t("about.pickup")}
                </h3>
              </AccordionTrigger>
              <AccordionContent>
                {checkPickupHoursRemainSameAsOpeningHours === true ? (
                  <p>{t("about.same_as_opening_hours")}</p>
                ) : (
                  <div className="space-y-2">
                    {pickupHours.map((day) => (
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">{day.day}</span>
                        <div className="text-right">
                          {day.hours.map((hour) => (
                            <div className="">{hour}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* table reservation card */}
          <Accordion type="single" collapsible>
            <AccordionItem
              value={t("about.table_reservation")}
              className="border-b-0"
            >
              <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
                <h3 className="text-red font-semibold text-lg sm:text-xl">
                  {t("about.table_reservation")}
                </h3>
              </AccordionTrigger>
              <AccordionContent>
                {checkTableReservationHoursRemainSameAsOpeningHours === true ? (
                  <p>{t("about.same_as_opening_hours")}</p>
                ) : (
                  <div className="space-y-2">
                    {tableReservationHours.map((day) => (
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">{day.day}</span>
                        <div className="text-right">
                          {day.hours.map((hour) => (
                            <div className="">{hour}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* order ahead card */}
          <Accordion type="single" collapsible>
            <AccordionItem
              value={t("about.order_ahead")}
              className="border-b-0"
            >
              <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
                <h3 className="text-red font-semibold text-lg sm:text-xl">
                  {t("about.order_ahead")}
                </h3>
              </AccordionTrigger>
              <AccordionContent>
                <p>{t("about.same_as_opening_hours")}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* payment methods card */}
        <Accordion type="single" collapsible>
          <AccordionItem
            value={t("about.payment_methods")}
            className="border-b-0"
          >
            <AccordionTrigger className="p-0 font-medium transition-all hover:no-underline [&>svg]:size-5 gap-3">
              <h3 className="text-red font-semibold text-lg sm:text-xl">
                {t("about.payment_methods")}
              </h3>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                {paymentMethodsArray.map((item, index) => (
                  <div className="flex gap-2 items-center [&>svg]:size-4 [&>svg]:sm:size-5 [&>svg]:flex-shrink-0 text-sm sm:text-base">
                    <item.icon />
                    <p>{item.label}</p>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* address and phone grid */}
        <div className="grid lg:grid-cols-2 gap-5">
          {/* address */}
          <div className="space-y-2.5">
            <h3 className="text-red font-semibold text-lg sm:text-xl">
              {t("about.address")}
            </h3>
            <div className="flex gap-2 items-center text-sm sm:text-base">
              <MapPin className="flex-shrink-0 size-4 sm:size-5" />
              <p>
                {/* {address} */}
                {address
                  ?.replace(/\\n/g, "\n") // Replace literal '\\n' with actual new line
                  .split("\n") // Split the string by actual new lines
                  .map((line, index) => (
                    <span key={index}>
                      {line}
                      <br />
                    </span>
                  ))}
              </p>
            </div>
          </div>

          {/* phone */}
          <div className="space-y-2.5">
            <h3 className="text-red font-semibold text-lg sm:text-xl">
              {t("about.phone")}
            </h3>
            <div className="flex gap-2 items-center text-sm sm:text-base">
              <Phone className="flex-shrink-0 size-4 sm:size-5" />
              <p>{phoneNumber}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
